import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Card,
  Button,
  Badge,
  Spinner,
  makeStyles,
  tokens,
  Select,
  Option,
} from '@fluentui/react-components'
import {
  TicketDiagonal24Regular,
  ArrowRight24Regular,
  CheckmarkCircle24Regular,
  Clock24Regular,
  Warning24Regular,
  Open24Regular,
} from '@fluentui/react-icons'
import { api, Ticket } from '../lib/api'
import { useConfig } from '../context/ConfigContext'
import { ticketStatusColor, ticketStatusLabel } from '../lib/ticket-status'

const useStyles = makeStyles({
  root: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  filters: {
    display: 'flex',
    gap: '12px',
  },
  ticketList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  ticketCard: {
    padding: '16px 20px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  ticketHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  ticketTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  ticketNumber: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontWeight: '500',
  },
  ticketSubject: {
    fontSize: '15px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  ticketMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '8px',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  lastMessage: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    marginTop: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 24px',
    textAlign: 'center',
  },
  emptyIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    margin: '0 0 8px 0',
  },
  emptyMessage: {
    color: tokens.colorNeutralForeground2,
    margin: '0 0 24px 0',
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginTop: '24px',
  },
  paginationInfo: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  ticketTypeBadge: {
    fontSize: '11px',
  },
})

const statusIcons: Record<string, typeof Open24Regular> = {
  new: Open24Regular,
  open: Clock24Regular,
  pending: Warning24Regular,
  resolved: CheckmarkCircle24Regular,
  closed: CheckmarkCircle24Regular,
}

export function MyTickets() {
  const styles = useStyles()
  const navigate = useNavigate()
  const { config } = useConfig()

  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false,
  })

  useEffect(() => {
    fetchTickets()
    // fetchTickets is scoped to the current render filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter])

  const fetchTickets = async (offset = 0) => {
    setLoading(true)
    try {
      const result = await api.getTickets({
        status: statusFilter === 'all' ? undefined : statusFilter,
        ticketType: typeFilter === 'all' ? undefined : typeFilter,
        limit: 20,
        offset,
      })
      setTickets(result.tickets)
      setPagination(result.pagination)
    } catch (error) {
      console.error('Failed to fetch tickets:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60))
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60))
        return minutes <= 1 ? 'Just now' : `${minutes} minutes ago`
      }
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`
    }
    if (days === 1) return 'Yesterday'
    if (days < 7) return `${days} days ago`
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    })
  }

  const getStatusConfig = (ticket: Ticket) => {
    return {
      label: ticket.statusLabel || ticketStatusLabel(ticket.status, config?.statusLabels),
      color: ticketStatusColor(ticket.status),
      icon: statusIcons[ticket.status] || statusIcons.new,
    }
  }

  if (loading && tickets.length === 0) {
    return (
      <div className={styles.root}>
        <div className={styles.loadingState}>
          <Spinner size="large" label="Loading tickets..." />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h2 className={styles.title}>My Tickets</h2>
        <div className={styles.filters}>
          <Select
            value={typeFilter}
            onChange={(_, data) => setTypeFilter(data.value)}
          >
            <Option value="all">All Types</Option>
            <Option value="support">Support</Option>
            <Option value="it_request">IT Requests</Option>
          </Select>
          <Select
            value={statusFilter}
            onChange={(_, data) => setStatusFilter(data.value)}
          >
            <Option value="all">All Statuses</Option>
            <Option value="open">{ticketStatusLabel('open', config?.statusLabels)}</Option>
            <Option value="closed">{ticketStatusLabel('closed', config?.statusLabels)}</Option>
          </Select>
        </div>
      </div>

      {tickets.length === 0 ? (
        <Card className={styles.emptyState}>
          <TicketDiagonal24Regular className={styles.emptyIcon} />
          <h3 className={styles.emptyTitle}>No tickets found</h3>
          <p className={styles.emptyMessage}>
            {statusFilter === 'all'
              ? "You haven't submitted any support requests yet."
              : `No ${ticketStatusLabel(statusFilter, config?.statusLabels)} tickets found.`}
          </p>
          <Button appearance="primary" onClick={() => navigate('/submit')}>
            Submit a Request
          </Button>
        </Card>
      ) : (
        <>
          <div className={styles.ticketList}>
            {tickets.map(ticket => {
              const status = getStatusConfig(ticket)
              const StatusIcon = status.icon

              return (
                <Card
                  key={ticket.id}
                  className={styles.ticketCard}
                  onClick={() => navigate(`/tickets/${ticket.id}`)}
                >
                  <div className={styles.ticketHeader}>
                    <div className={styles.ticketTitle}>
                      <span className={styles.ticketNumber}>{ticket.ticketNumber}</span>
                      {ticket.ticketType === 'it_request' && (
                        <Badge
                          appearance="tint"
                          color="brand"
                          size="small"
                          className={styles.ticketTypeBadge}
                        >
                          IT Request
                        </Badge>
                      )}
                      <h3 className={styles.ticketSubject}>{ticket.subject}</h3>
                    </div>
                    <Badge color={status.color} appearance="filled" icon={<StatusIcon />}>
                      {status.label}
                    </Badge>
                  </div>

                  {ticket.lastMessage && (
                    <p className={styles.lastMessage}>{ticket.lastMessage}</p>
                  )}

                  <div className={styles.ticketMeta}>
                    <span className={styles.metaItem}>
                      <Clock24Regular />
                      {formatDate(ticket.createdAt)}
                    </span>
                    {ticket.messageCount && ticket.messageCount > 1 && (
                      <span className={styles.metaItem}>
                        {ticket.messageCount} messages
                      </span>
                    )}
                    <span className={styles.metaItem} style={{ marginLeft: 'auto' }}>
                      View details
                      <ArrowRight24Regular />
                    </span>
                  </div>
                </Card>
              )
            })}
          </div>

          {(pagination.hasMore || pagination.offset > 0) && (
            <div className={styles.pagination}>
              <Button
                appearance="secondary"
                disabled={pagination.offset === 0 || loading}
                onClick={() => fetchTickets(Math.max(0, pagination.offset - pagination.limit))}
              >
                Previous
              </Button>
              <span className={styles.paginationInfo}>
                Showing {pagination.offset + 1}-{Math.min(pagination.offset + tickets.length, pagination.total)} of {pagination.total}
              </span>
              <Button
                appearance="secondary"
                disabled={!pagination.hasMore || loading}
                onClick={() => fetchTickets(pagination.offset + pagination.limit)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
